/**
 * paire-app#128 — Allergy-accommodation badges on the restaurant profile.
 *
 * Every member-facing string the `AllergyAccommodations` card renders lives
 * here. The badges reflect what the outlet selected on its PRO dashboard
 * (Dietary Accommodations keyword group) — a self-declared accommodation,
 * never a Paire-verified food-safety claim.
 */

export const allergyAccommodationsCopy = {
  /** The card's single label (rendered as the card's heading, 18px bold). */
  label: 'Allergy Accommodations',
  /**
   * Caption below the badge grid. D57b's CD Decision 1 scopes every
   * allergen-adjacent claim on the platform to what the underlying data
   * actually supports and bars any cross-contamination/shared-kitchen/
   * food-safety guarantee — this card's data is the restaurant's own
   * PRO-dashboard selection, so the line attributes the claim to that
   * source and sends the member to staff before they order.
   */
  disclaimer: 'Shared by the restaurant — always confirm allergies with your server before you order.',
  /**
   * Shown under an all-gray grid when an outlet with an enhanced profile has
   * selected no allergens (paire-app#128 founder follow-up). Says the outlet
   * hasn't shared anything — never that it accommodates nothing.
   */
  emptyState: "This restaurant hasn't shared allergy accommodations yet.",
  /**
   * The disclaimer's empty-state form. "Shared by the restaurant" would
   * contradict the line above it when nothing was shared, so only the
   * confirm-with-your-server instruction remains.
   */
  emptyStateDisclaimer: 'Always confirm allergies with your server before you order.',
} as const;

/**
 * Short badge labels, one per allergen, in the order of the reference design
 * (paire-app#94). Rendered uppercase under each icon.
 */
export const allergenLabels = {
  shellfish: 'Shellfish',
  seafood: 'Seafood',
  wheat: 'Wheat',
  sesame: 'Sesame',
  peanut: 'Peanut',
  treeNut: 'Tree Nut',
  egg: 'Egg',
  dairy: 'Dairy',
  soy: 'Soy',
} as const;

/**
 * Tooltip + accessible name for an allergen the outlet accommodates.
 * Josh's wording, verbatim shape: "Accommodates Shellfish Allergies".
 */
export const accommodatesAllergyLabel = (allergen: string): string =>
  `Accommodates ${allergen} Allergies`;

/**
 * Tooltip + accessible name for an allergen the outlet did NOT select.
 * Deliberately says "not listed" rather than "not accommodated": an unselected
 * badge only means the outlet didn't claim it, never that it can't.
 */
export const allergyNotListedLabel = (allergen: string): string =>
  `${allergen} allergies not listed by this restaurant`;
